import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, X } from "lucide-react";
import React from "react";
import { toast } from "sonner";

import * as api from "@/api/supply";
import * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-errors";
import { useUrlParam } from "@/lib/url-state";
import { cn } from "@/lib/utils";
import { fmtDateTime } from "../format";
import {
  ACTIVE_SLOT_WATCHES,
  REGISTRY,
  RUNS_KEY,
  registryKey,
} from "./ozonQueries";
import {
  CHIPS,
  applyFilter,
  chipCounts,
  chipOf,
  chooseSort,
  inChip,
  ONEC_DOC_LABEL,
  lastRefreshedAt,
  ozonStateLabel,
  sortRows,
  type RegistrySort,
} from "./registryModel";
import { RegistryTable, type RunInfo } from "./RegistryTable";
import { useOzonDirectionActions } from "./useOzonDirectionActions";

const SELECT = "h-8 rounded border border-input bg-background px-2 text-sm";

/** Реестр поставок Ozon по ВСЕМ фиксациям (вариант A, владелец 15.10.2026).
 *
 *  Фиксация — колонка и фильтр, а не выпадающий список «какую открыть»: поставки живут неделями,
 *  и пока список показывал одну фиксацию, брошенные в старых не видел никто (фиксация #4 —
 *  18 из 20 не начаты). Работа с поставкой — на её СТРАНИЦЕ (`/supply/ozon/:id`); здесь только
 *  «Спросить площадку» (обратимо), архив и удаление фиксации.
 *
 *  ⚠️ Статусы площадки и состояние документов 1С — ТОЛЬКО кнопкой «Обновить статусы»: само по
 *  себе на экране ничего не меняется, чтобы человек не путался (решение владельца). Ни
 *  автозапуска, ни фона.
 *
 *  ⚠️ Всё состояние — в адресе: со страницы поставки возвращаются ссылкой `?tab=ozon&run=N`, и
 *  она обязана открыть тот же срез.
 */
export function OzonSuppliesPage(): React.ReactElement {
  const qc = useQueryClient();
  const [status, setStatus] = useUrlParam("status");
  const [runAsked, setRunAsked] = useUrlParam("run");
  const [legal, setLegal] = useUrlParam("legal");
  const [q, setQ] = useUrlParam("q");
  const [groupAsked, setGroupAsked] = useUrlParam("group");
  const grouped = groupAsked === "1";
  const chip = chipOf(status);
  const [sortAsked, setSortAsked] = useUrlParam("sort");
  const sort = chooseSort(sortAsked);
  const setSort = (s: RegistrySort): void => setSortAsked(s === "run" ? "" : s);
  const [report, setReport] = React.useState<ozon.RefreshResult | null>(null);

  const live = useQuery({
    queryKey: registryKey(false),
    queryFn: () => ozon.getOzonRegistry(false),
  });
  // архив — отдельным запросом и только когда его открыли: завершённые копятся без конца
  const arch = useQuery({
    queryKey: registryKey(true),
    queryFn: () => ozon.getOzonRegistry(true),
    enabled: chip === "archive",
  });
  const runs = useQuery({ queryKey: RUNS_KEY, queryFn: api.getShipmentRuns });
  // отметка «мониторинг мест» у строк — один запрос на заход, без опроса (как и статусы площадки)
  const watches = useQuery({
    queryKey: ACTIVE_SLOT_WATCHES,
    queryFn: () => ozon.getSlotWatches("active"),
  });
  const watched = React.useMemo(
    () =>
      watches.data
        ? new Map(watches.data.map((w) => [w.shipment_id, w]))
        : null,
    [watches.data],
  );

  // ⚠️ `?run=` — ПОЖЕЛАНИЕ: фиксацию могли удалить, пока человек был на странице поставки. Про
  // исчезнувшую — сказать вслух и снять фильтр, иначе экран молча показывал бы пустоту
  const [lost, setLost] = React.useState<string | null>(null);
  const runKnown = runs.data?.some((r) => String(r.id) === runAsked) ?? false;
  React.useEffect(() => {
    if (!runAsked || !runs.data || runKnown) return;
    setLost(runAsked);
    setRunAsked("");
    // setRunAsked меняет идентичность на каждый рендер — в зависимостях ему делать нечего
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runAsked, runs.data, runKnown]);

  const act = useOzonDirectionActions();
  const now = Date.now();

  const refresh = useMutation({
    mutationFn: ozon.refreshOzonStatuses,
    onSuccess: (r) => {
      setReport(r);
      toast.success(
        `Ozon: проверено ${r.checked}, изменилось ${r.changed.length} · ` +
          `1С: проверено ${r.onec_checked}, изменилось ${r.onec_changed.length}`,
      );
      // ⚠️ отказ кабинета, пропавшая заявка и недоступная 1С — не «мелочь в логе»: у этих
      // поставок статус старый
      if (r.errors.length > 0)
        toast.error(`Не всё обновилось: ${r.errors.length}`, {
          description: r.errors.join("\n"),
        });
      void qc.invalidateQueries({ queryKey: REGISTRY, refetchType: "all" });
    },
    onError: (e) =>
      toast.error(extractErrorMessage(e, "Статусы не обновились")),
  });

  const archive = useMutation({
    mutationFn: (v: { id: number; back: boolean }) =>
      v.back
        ? ozon.unarchiveOzonShipment(v.id)
        : ozon.archiveOzonShipment(v.id),
    onSuccess: (row, v) => {
      toast.success(
        `${row.region}: ${v.back ? "возвращена из архива" : "убрана в архив"}`,
      );
      void qc.invalidateQueries({ queryKey: REGISTRY, refetchType: "all" });
    },
    onError: (e, v) =>
      toast.error(
        extractErrorMessage(
          e,
          v.back ? "Не вернули из архива" : "Не убрали в архив",
        ),
      ),
  });

  const delRun = useMutation({
    mutationFn: (id: number) => api.deleteShipmentRun(id),
    onSuccess: (_, id) => {
      toast.success(`Фиксация #${id} удалена`);
      // фильтр по удалённой фиксации снимаем сразу: иначе экран скажет «больше не существует»
      if (runAsked === String(id)) setRunAsked("");
      void qc.invalidateQueries({ queryKey: REGISTRY, refetchType: "all" });
      void qc.invalidateQueries({ queryKey: RUNS_KEY });
    },
    // 409 — поставка с живой заявкой Ozon: текст сервера и есть ответ, что мешает
    onError: (e) => toast.error(extractErrorMessage(e, "Фиксация не удалена")),
  });

  const runInfo = (runId: number): RunInfo => ({
    total: runs.data?.find((r) => r.id === runId)?.shipments_count ?? null,
    onDelete: (id, shown) => {
      if (
        window.confirm(
          `Удалить фиксацию #${id} со всеми поставками (в списке ${shown})? ` +
            "Поставки с живой заявкой Ozon удалить не дадут — сначала отмените заявку.",
        )
      )
        delRun.mutate(id);
    },
    deleting: (id) => delRun.isPending && delRun.variables === id,
  });

  const counts = chipCounts(live.data ?? [], arch.data, now);
  const current = chip === "archive" ? arch : live;
  const base =
    chip === "archive"
      ? arch.data
      : live.data?.filter((r) => inChip(r, chip, now));
  const shown =
    base &&
    sortRows(
      applyFilter(base, {
        legal,
        run: runAsked ? Number(runAsked) : null,
        q,
      }),
      sort,
    );

  // юрлица — из того, что пришло; выбранное держим в списке, даже если строк с ним нет: браузер
  // рисует `<select>` с чужим значением ПЕРВЫМ пунктом, и экран врал бы, что фильтра нет
  const legals = [
    ...new Set([
      ...(live.data ?? []).map((r) => r.legal_name),
      ...(arch.data ?? []).map((r) => r.legal_name),
      ...(legal ? [legal] : []),
    ]),
  ].sort((a, b) => a.localeCompare(b, "ru"));
  const refreshedAt = lastRefreshedAt([
    ...(live.data ?? []),
    ...(arch.data ?? []),
  ]);

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">Поставки Ozon</h2>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Все поставки по всем фиксациям. Статусы площадки и состояние
          документов 1С обновляются только кнопкой. Черновик обратим и в кабинете не виден;{" "}
          <b>«доступен» не значит «возьмут всё»</b>.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {CHIPS.map((c) => (
          <ChipButton
            key={c.key}
            active={chip === c.key}
            title={c.title}
            count={counts[c.key]}
            warn={c.key === "attention" && (counts.attention ?? 0) > 0}
            onClick={() => setStatus(c.key === "active" ? "" : c.key)}
          >
            {c.label}
          </ChipButton>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm">
        <select
          className={SELECT}
          value={legal}
          onChange={(e) => setLegal(e.target.value)}
          aria-label="Юрлицо"
        >
          <option value="">все юрлица</option>
          {legals.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        <select
          className={SELECT}
          value={runKnown ? runAsked : ""}
          onChange={(e) => {
            setLost(null);
            setRunAsked(e.target.value);
          }}
          aria-label="Фиксация"
        >
          <option value="">все фиксации</option>
          {(runs.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              #{r.id} · {fmtDateTime(r.created_at)} · {r.created_by} · поставок{" "}
              {r.shipments_count}
            </option>
          ))}
        </select>
        <Input
          className="h-8 w-48"
          placeholder="регион…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Поиск по региону"
        />
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={grouped}
            onChange={(e) => setGroupAsked(e.target.checked ? "1" : "")}
          />
          группировать по фиксации
        </label>
        <div className="ml-auto flex items-center gap-2">
          <span
            className="text-xs text-muted-foreground"
            title="Самое свежее время, когда портал спрашивал у площадки статус заявки"
          >
            {refreshedAt
              ? `обновлено ${fmtDateTime(refreshedAt)}`
              : "статусы не обновлялись"}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            disabled={refresh.isPending}
            title="Спросить у площадки статусы всех заявок, а у 1С — состояние документов (заказ, ПТМО, СФ). Само по себе не обновляется"
            onClick={() => refresh.mutate()}
          >
            <RefreshCw
              className={cn("size-4", refresh.isPending && "animate-spin")}
            />
            Обновить статусы
          </Button>
        </div>
      </div>

      {watches.isError && (
        <p className="text-sm text-red-600 dark:text-red-400">
          ⚠ Мониторинги мест не прочитались — отметок «мониторинг мест» в таблице нет:{" "}
          {extractErrorMessage(watches.error, "ошибка")}
        </p>
      )}

      {runs.isError && (
        <p className="text-sm text-red-600 dark:text-red-400">
          ⚠ {extractErrorMessage(runs.error, "Фиксации не пришли")}
        </p>
      )}

      {lost !== null && (
        <p className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          ⚠ Фиксация #{lost} больше не существует — фильтр по фиксации снят.
        </p>
      )}

      {report && (
        <RefreshReport report={report} onClose={() => setReport(null)} />
      )}

      {current.isError && (
        <p className="text-sm text-red-600 dark:text-red-400">
          ⚠ {extractErrorMessage(current.error, "Поставки Ozon не пришли")}
        </p>
      )}
      {current.isLoading && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Загрузка…
        </p>
      )}

      {shown && shown.length === 0 && (
        <p className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">
          {base?.length === 0
            ? chip === "archive"
              ? "Архив пуст."
              : chip === "active"
                ? "Поставок Ozon нет — сначала зафиксируйте распределение."
                : "В этом статусе поставок нет."
            : "Под фильтр ничего не попало."}
        </p>
      )}

      {shown && shown.length > 0 && (
        <RegistryTable
          rows={shown}
          grouped={grouped}
          sort={sort}
          onSort={setSort}
          now={now}
          watched={watched}
          runInfo={runInfo}
          act={{
            draft: act.draft,
            archive: (id, back) => archive.mutate({ id, back }),
            busy: (id) =>
              act.busy(id) ||
              (archive.isPending && archive.variables?.id === id),
          }}
        />
      )}
    </div>
  );
}

function ChipButton({
  active,
  count,
  warn,
  title,
  onClick,
  children,
}: {
  active: boolean;
  /** null — число неизвестно (архив ещё не открывали) */
  count: number | null;
  warn: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-input bg-background hover:bg-accent",
      )}
    >
      {children}
      {count !== null && (
        <span
          className={cn(
            "rounded-full px-1.5 text-xs tabular-nums",
            active
              ? "bg-primary-foreground/20"
              : warn
                ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                : "bg-muted text-muted-foreground",
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

/** «→ подпись n» по убыванию — сводка «куда перешли». */
function summarize(afters: string[]): string {
  const counts = new Map<string, number>();
  for (const a of afters) counts.set(a, (counts.get(a) ?? 0) + 1);
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, n]) => `→ ${label} ${n}`)
    .join(" · ");
}

/** Итог последнего обновления статусов (Ozon + 1С): что изменилось и что НЕ обновилось. Висит
 *  до закрытия — тост уходит через секунды, а ошибки кабинета и 1С надо успеть прочитать. */
function RefreshReport({
  report,
  onClose,
}: {
  report: ozon.RefreshResult;
  onClose: () => void;
}): React.ReactElement {
  const bad = report.errors.length > 0;
  const onec = report.onec_changed;
  return (
    <div
      className={cn(
        "relative rounded-md border px-3 py-2 pr-8 text-sm",
        bad
          ? "border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
          : "bg-card",
      )}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 rounded p-0.5 text-muted-foreground hover:bg-accent"
        title="Скрыть"
      >
        <X className="size-4" />
      </button>
      <p>
        Статусы Ozon на {fmtDateTime(report.refreshed_at)}: проверено{" "}
        {report.checked}, изменилось {report.changed.length}
        {bad && `, не обновилось — ${report.errors.length}`}.
      </p>
      {report.changed.length > 0 && (
        <>
          <p className="mt-1 text-xs" data-testid="refresh-summary">
            {summarize(
              report.changed.map((c) => ozonStateLabel(c.after) ?? c.after),
            )}
          </p>
          <details className="mt-1 text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              подробнее ({report.changed.length})
            </summary>
            <ul className="mt-1 list-disc pl-5">
              {report.changed.map((c) => (
                <li key={c.shipment_id}>
                  {c.region} · {c.legal_name}
                  {c.supply_number ? ` · №${c.supply_number}` : ""}:{" "}
                  {ozonStateLabel(c.before) ?? "не обновлялся"} →{" "}
                  {ozonStateLabel(c.after)}
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
      <p className="mt-1" data-testid="refresh-onec">
        1С: проверено {report.onec_checked}, изменилось {onec.length}.
      </p>
      {onec.length > 0 && (
        <>
          <p className="mt-1 text-xs" data-testid="refresh-onec-summary">
            {summarize(onec.map((c) => c.after))}
          </p>
          <details className="mt-1 text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              1С: подробнее ({onec.length})
            </summary>
            <ul className="mt-1 list-disc pl-5">
              {onec.map((c) => (
                <li key={`${c.shipment_id}-${c.doc}`}>
                  {c.region} · {c.legal_name}
                  {c.supply_number ? ` · №${c.supply_number}` : ""}:{" "}
                  {ONEC_DOC_LABEL[c.doc].short} {c.number}:{" "}
                  {c.before ?? "не запрашивалось"} → {c.after}
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
      {bad && (
        <ul className="mt-1 list-disc pl-5 text-xs">
          {report.errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
